import { BadgeColor, BadgeVariant } from '@ichoosr-core/ui-data';
import '@angular/localize/init';

export enum DealStage {
  Unknown = 'Unknown',
  AssessmentPending = 'AssessmentPending',
  FinalOfferSent = 'FinalOfferSent',
  FinalOfferApproved = 'FinalOfferApproved',
  InstallationDateScheduled = 'InstallationDateScheduled',
  InstallationPerformed = 'InstallationPerformed',
  DeliveryApprovalRequested = 'DeliveryApprovalRequested',
  DeliveryApprovalRequestDeclined = 'DeliveryApprovalRequestDeclined',
  DeliveryApproved = 'DeliveryApproved',
  DeliveryCancellationRequested = 'DeliveryCancellationRequested',
  DeliveryCancelled = 'DeliveryCancelled',
  DeliveryCancellationRequestDeclined = 'DeliveryCancellationRequestDeclined',
}

const DEAL_STAGE_LABELS = {
  [DealStage.Unknown]: $localize`:@@dealStage.unknown:Unknown`,
  [DealStage.AssessmentPending]: $localize`:@@dealStage.assessmentPending:Assessment`,
  [DealStage.FinalOfferSent]: $localize`:@@dealStage.finalOfferSent:Offer Sent`,
  [DealStage.FinalOfferApproved]: $localize`:@@dealStage.finalOfferApproved:Offer Signed`,
  [DealStage.InstallationDateScheduled]: $localize`:@@dealStage.installationDateScheduled:Scheduled`,
  [DealStage.InstallationPerformed]: $localize`:@@dealStage.installationPerformed:Installed`,
  [DealStage.DeliveryApprovalRequested]: $localize`:@@dealStage.deliveryApprovalRequested:Approval Requested`,
  [DealStage.DeliveryApprovalRequestDeclined]: $localize`:@@dealStage.deliveryApprovalRequestDeclined:Declined`,
  [DealStage.DeliveryApproved]: $localize`:@@dealStage.deliveryApproved:Approved`,
  [DealStage.DeliveryCancellationRequested]: $localize`:@@dealStage.deliveryCancellationRequested:Cancel Requested`,
  [DealStage.DeliveryCancellationRequestDeclined]: $localize`:@@dealStage.deliveryCancellationRequestDeclined:Cancel Declined`,
  [DealStage.DeliveryCancelled]: $localize`:@@dealStage.deliveryCancelled:Cancelled`,
};

const BADGE_VARIANT_DEAL_STAGE_MAP: Record<DealStage, BadgeVariant> = {
  [DealStage.Unknown]: 'transparent',
  [DealStage.AssessmentPending]: 'transparent',
  [DealStage.FinalOfferSent]: 'transparent',
  [DealStage.FinalOfferApproved]: 'transparent',
  [DealStage.InstallationDateScheduled]: 'transparent',
  [DealStage.InstallationPerformed]: 'transparent',
  [DealStage.DeliveryApprovalRequested]: 'background',
  [DealStage.DeliveryApprovalRequestDeclined]: 'transparent',
  [DealStage.DeliveryApproved]: 'background',
  [DealStage.DeliveryCancellationRequested]: 'transparent',
  [DealStage.DeliveryCancellationRequestDeclined]: 'transparent',
  [DealStage.DeliveryCancelled]: 'background',
};

const BADGE_COLOR_DEAL_STAGE_MAP: Record<DealStage, BadgeColor> = {
  [DealStage.Unknown]: 'primary',
  [DealStage.AssessmentPending]: 'primary',
  [DealStage.FinalOfferSent]: 'primary',
  [DealStage.FinalOfferApproved]: 'primary',
  [DealStage.InstallationDateScheduled]: 'primary',
  [DealStage.InstallationPerformed]: 'success',
  [DealStage.DeliveryApprovalRequested]: 'information',
  [DealStage.DeliveryApprovalRequestDeclined]: 'error',
  [DealStage.DeliveryApproved]: 'success',
  [DealStage.DeliveryCancellationRequested]: 'warning',
  [DealStage.DeliveryCancellationRequestDeclined]: 'warning',
  [DealStage.DeliveryCancelled]: 'error',
};

/**
 * Returns a label for the deal stage
 * @param dealStage The deal stage
 * @returns The label for the deal stage
 */
export function getLabelForDealStage(dealStage: DealStage): string {
  return DEAL_STAGE_LABELS[dealStage] || 'Unknown';
}

/**
 * Returns a badge variant for the deal stage
 * @param dealStage The deal stage
 * @returns The badge variant for the deal stage
 */
export function getBadgeVariantForDealStage(dealStage: DealStage): BadgeVariant {
  return BADGE_VARIANT_DEAL_STAGE_MAP[dealStage];
}

/**
 * Returns a badge color for the deal stage
 * @param dealStage The deal stage
 * @returns The badge color for the deal stage
 */
export function getBadgeColorForDealStage(dealStage: DealStage): BadgeColor {
  return BADGE_COLOR_DEAL_STAGE_MAP[dealStage];
}
